/**
 * Duration cell for a catalog sync run (Run History + the dashboard's recent runs).
 *
 * Never counts up to now, and never presents a timestamp that is not the run's
 * end as if it were:
 *   - RUNNING                      → "running"
 *   - no completedAt               → "no end time"
 *   - closed by the DEC-318 sweeper → "no end time". The sweeper stamps
 *     completed_at with the time of the sweep (often days later), not the time
 *     the task was killed, so end − start there is how long until somebody
 *     noticed, not how long the run took ("4320m" on dev, 2026-10-15 walk).
 *   - FAILED                       → "failed after 12m"
 *   - otherwise                    → "2h 15m"
 */
import { formatDurationBetween } from '@lodex-pro/web-shared';

/**
 * The prefix of the error_message `failStaleRuns` writes
 * (infrastructure/catalog-sync/src/runner.ts). It is the only record that a
 * run was closed by the sweeper rather than by the runner itself.
 */
export const SWEPT_RUN_ERROR_PREFIX = 'task killed before it could record a failure';

export interface RunTiming {
  readonly status: string | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly errorMessage: string | null;
}

export function runDurationLabel(run: RunTiming): string {
  if (run.status === 'RUNNING') return 'running';
  if (run.completedAt === null) return 'no end time';
  if (run.errorMessage?.startsWith(SWEPT_RUN_ERROR_PREFIX) === true) return 'no end time';
  const elapsed = formatDurationBetween(run.startedAt, run.completedAt);
  return run.status === 'FAILED' ? `failed after ${elapsed}` : elapsed;
}
